import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import omit from 'lodash/omit';
import { type IntlShape, useIntl } from 'react-intl-next';
import uuid from 'uuid/v4';

import type { EditorActions } from '@atlaskit/editor-core';
import { token } from '@atlaskit/tokens';
import {
	checkItems,
	type HubDefaultParameters,
	updateNodeParameters,
	type UseExtensionParametersAndActionsProps,
	useExtensionsActions,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import { CAROUSEL_VERSION_2 } from '../../constants';
import {
	ButtonStyle,
	type CarouselParameters,
	CommonLayout,
	CornerStyle,
	type Frame,
	FRAME_FIELDS,
	NavigationStyle,
} from '../../types';

import { messages } from './messages';
import type { CarouselActions, UpdateField } from './types';

/** This hook has been created newly for Carousel v2 development
 * We intend to decouple Cards and Carousel extensions as much as we can,
 * as migration from Confluence to Platform will be more simple
 * Much refactoring has been done with reference to cards as frames in v2 design spec
 * Where possible, we resort to referring to cards and frames
 * However, when directly referring to param attribute, we will continue to use the naming cards and cardId
 * This is because Carousel v1 macros params will need to be mapped to the frames and frameId naming, which if can be hazardous
 */

export const carouselNewParametersV2 = {
	cornerStyle: CornerStyle.ROUNDED,
	isAutoplayEnabled: false,
	navigationStyle: NavigationStyle.THUMBNAILS,
};

export const FRAME_V2_DEFAULT_IMAGE_SRC =
	'https://images.unsplash.com/photo-1488470015861-8bb1317353ef?auto=format&fit=crop&q=80&w=1080';

export const frameNewParametersV2 = {
	[FRAME_FIELDS.BACKGROUND_COLOR]: token('color.background.accent.blue.subtlest'),
	[FRAME_FIELDS.TEXT_COLOR]: token('color.text'),
	[FRAME_FIELDS.LAYOUT]: CommonLayout.SPLIT_IMAGE_RIGHT,
	[FRAME_FIELDS.IMAGE_SRC]: FRAME_V2_DEFAULT_IMAGE_SRC,
	[FRAME_FIELDS.IMAGE_ALT_TEXT]: 'Close-up of a vibrant green palm leaf’s patterns.',
	buttonText: '',
	buttonStyle: ButtonStyle.DEFAULT,
};

export const initialUnsplashImage: Partial<Frame> = {
	imageSrc:
		'https://images.unsplash.com/photo-1579547621706-1a9c79d5c9f1?auto=format&fit=crop&q=80&w=1080',
	imageAltText: 'purple and pink light illustration',
};

export const createNewFrameWithDefaultParameters = ({
	intl,
	defaultField,
	randomLayout,
	defaultParameters,
}: {
	defaultField?: UpdateField<Frame>;
	defaultParameters?: HubDefaultParameters;
	intl: IntlShape;
	randomLayout?: CommonLayout;
}): Frame => ({
	cardId: uuid(),
	link: '',
	isLinkUserEdited: false,
	title: intl.formatMessage(messages.placeholderTitle),
	isTitleUserEdited: false,
	description: intl.formatMessage(messages.placeholderDescription),
	isDescriptionUserEdited: false,
	...initialUnsplashImage,
	...frameNewParametersV2,
	...(defaultParameters?.backgroundColor
		? { [FRAME_FIELDS.BACKGROUND_COLOR]: defaultParameters.backgroundColor }
		: {}),
	...(defaultParameters?.textColor
		? { [FRAME_FIELDS.TEXT_COLOR]: defaultParameters.textColor }
		: {}),
	...(randomLayout && { [FRAME_FIELDS.LAYOUT]: randomLayout }),
	...defaultField,
});

export const updateLinkAndConfluencePageId = (
	link: string,
	confluencePageId?: string,
	isLinkUserEdited?: boolean,
) => {
	return {
		[FRAME_FIELDS.LINK]: link,
		[FRAME_FIELDS.CONFLUENCE_PAGE_ID]: confluencePageId || '',
		[FRAME_FIELDS.IS_LINK_USER_EDITED]: isLinkUserEdited || false,
	};
};

const fetchLocalIdMacroParameters = ({
	editorActions,
	localId,
	defaultParameters,
}: {
	defaultParameters?: HubDefaultParameters;
	editorActions: EditorActions;
	localId: string;
}): CarouselParameters => {
	const localIdNode = editorActions?.getNodeByLocalId?.(localId)?.toJSON();
	let params = localIdNode?.attrs?.parameters;

	if (params === undefined) {
		return undefined as any;
	}

	// Make a copy of the params object to avoid mutating the original object
	params = { ...params };

	// If the extension was created prior to removal of type field, remove it from object here
	// This update is/was not done in combination with a feature gate
	delete params['type'];

	params.version = CAROUSEL_VERSION_2;

	const { cornerStyle, isAutoplayEnabled, navigationStyle } = carouselNewParametersV2;
	params.cornerStyle ??= cornerStyle;
	params.isAutoplayEnabled ??= isAutoplayEnabled;
	params.navigationStyle ??= navigationStyle;

	/* construct frames array with intercepted params */
	params.cards =
		params.cards?.map((frame: Frame) => {
			return {
				...frameNewParametersV2,
				...(defaultParameters?.backgroundColor
					? { [FRAME_FIELDS.BACKGROUND_COLOR]: defaultParameters.backgroundColor }
					: {}),
				...(defaultParameters?.textColor
					? { [FRAME_FIELDS.TEXT_COLOR]: defaultParameters.textColor }
					: {}),
				// override BG color back to white for v1 carousels to maintain parity...
				[FRAME_FIELDS.BACKGROUND_COLOR]: token('color.background.input'),
				// ... but let frame BG color (if already set, eg to default color) override again
				...omit(frame, ['imagePosition']),
			};
		}) || params.cards;

	/* there were card fields added to carousel params that were nonfunctional.
	 * reconstructing params to those with them omitted
	 */
	params = omit(params, ['size', 'isPublishDateShown', 'isAvatarShown']);

	return params;
};

const updateFrameInList = (
	frames: Frame[],
	cardId: string,
	updatedField: UpdateField<Frame>,
): Frame[] =>
	frames.map((frame) => {
		if (frame.cardId === cardId) {
			return { ...frame, ...updatedField };
		}
		return frame;
	});

export type UseCarouselParametersAndActionsReturnType = {
	carouselActions: CarouselActions;
	carouselParameters: CarouselParameters;
};
export const useCarouselExtensionParametersAndActions = ({
	localId,
	editorActions,
}: UseExtensionParametersAndActionsProps): UseCarouselParametersAndActionsReturnType => {
	const { branding } = useProductDependencies();
	const defaultParameters = branding?.defaultParameters;

	const [carouselParameters, setCarouselParameters] = useState<CarouselParameters>(
		fetchLocalIdMacroParameters({ editorActions, localId, defaultParameters }),
	);
	const usedLayoutsRef = useRef<Set<CommonLayout>>(new Set());
	const lastUsedLayoutRef = useRef<CommonLayout | null>(null);
	const { setIsInAutoFocusMode, setSelectedToEditCardId } = useExtensionsActions();
	const { cards: frames } = carouselParameters ?? {};
	const intl = useIntl();

	useEffect(() => {
		const extensionNodeParams = fetchLocalIdMacroParameters({
			editorActions,
			localId,
			defaultParameters,
		});
		setCarouselParameters(extensionNodeParams);
		// we don't want to re-fetch the parameters when the default parameters change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorActions, localId]);

	const getRandomLayout = useCallback(() => {
		const allLayouts = Object.values(CommonLayout);

		if (usedLayoutsRef.current.size >= allLayouts.length - 1) {
			usedLayoutsRef.current = new Set();
		}

		const availableLayouts = allLayouts.filter(
			(layout) => !usedLayoutsRef.current.has(layout) && layout !== lastUsedLayoutRef.current,
		);

		const randomIndex = Math.floor(Math.random() * availableLayouts.length);
		const randomLayout = availableLayouts[randomIndex];
		usedLayoutsRef.current.add(randomLayout);
		lastUsedLayoutRef.current = randomLayout;

		return randomLayout;
	}, []);

	const updateExtensionParams = useCallback(
		(newExtensionParameters: UpdateField<CarouselParameters>) => {
			setCarouselParameters((prevParams) => {
				const updatedParams = {
					...prevParams,
					...newExtensionParameters,
				} as CarouselParameters;

				// Save params to ADF and state
				updateNodeParameters(editorActions, updatedParams);

				return updatedParams;
			});
		},
		[editorActions],
	);

	const addFrame = useCallback(() => {
		const newFrame = createNewFrameWithDefaultParameters({
			intl,
			defaultParameters,
			defaultField: undefined,
			randomLayout: getRandomLayout(),
		});
		updateExtensionParams({
			cards: [...(checkItems(frames) || []), newFrame],
		});
		setIsInAutoFocusMode(true);
		setSelectedToEditCardId(newFrame.cardId);
		// no need to re-run when default parameters change
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		frames,
		updateExtensionParams,
		setSelectedToEditCardId,
		setIsInAutoFocusMode,
		intl,
		getRandomLayout,
	]);

	const removeFrame = useCallback(
		(cardId: string) =>
			updateExtensionParams({
				cards: frames.filter((frame) => frame.cardId !== cardId),
			}),
		[frames, updateExtensionParams],
	);

	const updateMainFormField = useCallback(
		(updatedFields: UpdateField<CarouselParameters>) => updateExtensionParams(updatedFields),
		[updateExtensionParams],
	);

	const updateFrameFormField = useCallback(
		(cardId: string, updatedField: UpdateField<Frame>) => {
			const updatedFrames = updateFrameInList(frames, cardId, updatedField);

			updateExtensionParams({ cards: updatedFrames });
		},
		[frames, updateExtensionParams],
	);

	const carouselActions: CarouselActions = useMemo(
		() => ({
			addFrame,
			removeFrame,
			updateMainFormField,
			updateFrameFormField,
		}),
		[addFrame, removeFrame, updateMainFormField, updateFrameFormField],
	);

	return { carouselParameters, carouselActions };
};
